import type { FirebaseApp } from "firebase/app";
import type { Firestore } from "firebase/firestore";
import { localeDefinitions, siteContent } from "./generated/site-content";
import "./styles/main.css";

type Page = "home" | "privacy" | "support";
type LocaleCode = keyof typeof siteContent;
type Copy = (typeof siteContent)[LocaleCode];

const localeBySegment = new Map<string, (typeof localeDefinitions)[number]>(
  localeDefinitions.filter(({ segment }) => segment).map((locale) => [locale.segment, locale]),
);
const englishLocale = localeDefinitions.find(({ code }) => code === "en")!;

function currentRoute(): { locale: (typeof localeDefinitions)[number]; page: Page } {
  const segments = window.location.pathname.split("/").filter(Boolean);
  const localized = segments.length > 0 ? localeBySegment.get(segments[0]) : undefined;
  const locale = localized ?? englishLocale;
  const pageSegment = localized ? segments[1] : segments[0];
  const page: Page = pageSegment === "privacy" || pageSegment === "support" ? pageSegment : "home";
  return { locale, page };
}

function routeFor(locale: (typeof localeDefinitions)[number], page: Page): string {
  const prefix = locale.segment ? `/${locale.segment}` : "";
  if (page === "home") return prefix || "/";
  return `${prefix}/${page}`;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function link(className: string, text: string, href: string): HTMLAnchorElement {
  const anchor = element("a", className, text);
  anchor.href = href;
  return anchor;
}

function buildHeader(copy: Copy, locale: (typeof localeDefinitions)[number], page: Page): HTMLElement {
  const header = element("header", "site-header");
  const inner = element("div", "header-inner");
  const brand = link("brand", copy.appName, routeFor(locale, "home"));
  brand.setAttribute("aria-label", `${copy.appName} — ${copy["home-tagline"]}`);

  const controls = element("div", "header-controls");
  const nav = element("nav", "primary-nav");
  nav.setAttribute("aria-label", copy.appName);
  const privacy = link(page === "privacy" ? "active" : "", copy["privacy-link"], routeFor(locale, "privacy"));
  const support = link(page === "support" ? "active" : "", copy["support-link"], routeFor(locale, "support"));
  if (page === "privacy") privacy.setAttribute("aria-current", "page");
  if (page === "support") support.setAttribute("aria-current", "page");
  nav.append(privacy, support);

  const languageWrap = element("label", "language-select");
  const languageText = element("span", "visually-hidden", copy["language-label"]);
  const select = element("select");
  select.setAttribute("aria-label", copy["language-label"]);
  for (const optionLocale of localeDefinitions) {
    const option = element("option", "", siteContent[optionLocale.code]["language-name"]);
    option.value = optionLocale.code;
    option.selected = optionLocale.code === locale.code;
    select.append(option);
  }
  select.addEventListener("change", () => {
    const destination = localeDefinitions.find(({ code }) => code === select.value) ?? englishLocale;
    window.location.href = routeFor(destination, page);
  });
  languageWrap.append(languageText, select);
  controls.append(nav, languageWrap);
  inner.append(brand, controls);
  header.append(inner);
  return header;
}

function buildHome(copy: Copy): HTMLElement {
  const main = element("main", "home-page");
  const hero = element("section", "hero");
  const iconColumn = element("div", "icon-column");
  const iconFrame = element("div", "app-icon-frame");
  const icon = element("img");
  icon.src = "/icons/app-icon-512.png";
  icon.alt = "";
  icon.width = 512;
  icon.height = 512;
  iconFrame.append(icon);
  iconColumn.append(iconFrame);

  const copyColumn = element("div", "hero-copy");
  const title = element("h1", "display-title", copy.appName);
  const tagline = element("p", "tagline", copy["home-tagline"]);
  const description = element("p", "lead", copy["home-description"]);
  const convention = element("p", "body-copy", copy["home-convention"]);
  const audience = element("p", "body-copy", copy["home-audience"]);
  const availability = element("p", "availability", copy["home-availability"]);
  const actions = element("div", "hero-actions");
  const appStore = link("app-store-button", copy["app-store-cta"], import.meta.env.VITE_APP_STORE_URL || "#");
  if (import.meta.env.VITE_APP_STORE_URL) {
    appStore.target = "_blank";
    appStore.rel = "noopener noreferrer";
  } else {
    appStore.setAttribute("aria-disabled", "true");
  }
  actions.append(appStore);
  copyColumn.append(title, tagline, description, convention, audience, availability, actions);
  hero.append(iconColumn, copyColumn);
  main.append(hero);
  return main;
}

const privacySections = [
  "information", "analytics", "user-content", "support-form",
  "advertising", "sharing", "changes", "contact",
] as const;

function buildPrivacy(copy: Copy): HTMLElement {
  const main = element("main", "document-page");
  const article = element("article", "document-card");
  const header = element("header", "document-header");
  header.append(
    element("p", "section-kicker", copy.appName),
    element("h1", "page-title", copy["privacy-title"]),
    element("p", "updated", copy["privacy-updated"]),
    element("p", "document-intro", copy["privacy-intro"]),
  );
  article.append(header);
  for (const section of privacySections) {
    const block = element("section", "policy-section");
    block.append(
      element("h2", "", copy[`privacy-${section}-title`]),
      element("p", "", copy[`privacy-${section}-body`]),
    );
    article.append(block);
  }
  main.append(article);
  return main;
}

let firebaseApp: FirebaseApp | undefined;
let firestore: Firestore | undefined;
let firebaseServicesPromise: Promise<{
  database: Firestore;
  addDoc: typeof import("firebase/firestore").addDoc;
  collection: typeof import("firebase/firestore").collection;
  serverTimestamp: typeof import("firebase/firestore").serverTimestamp;
}> | undefined;

function supportServices() {
  firebaseServicesPromise ??= (async () => {
    const [appModule, appCheckModule, firestoreModule] = await Promise.all([
      import("firebase/app"),
      import("firebase/app-check"),
      import("firebase/firestore"),
    ]);
    if (!firebaseApp) {
      firebaseApp = appModule.initializeApp({
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      });
      const productionHost = window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1";
      if (productionHost && import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY) {
        appCheckModule.initializeAppCheck(firebaseApp, {
          provider: new appCheckModule.ReCaptchaEnterpriseProvider(import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY),
          isTokenAutoRefreshEnabled: true,
        });
      }
    }
    firestore ??= firestoreModule.getFirestore(firebaseApp);
    return {
      database: firestore,
      addDoc: firestoreModule.addDoc,
      collection: firestoreModule.collection,
      serverTimestamp: firestoreModule.serverTimestamp,
    };
  })();
  return firebaseServicesPromise;
}

async function messageFingerprint(email: string, category: string, message: string): Promise<string> {
  const input = new TextEncoder().encode(`${email.toLowerCase()}\n${category}\n${message}`);
  const digest = await crypto.subtle.digest("SHA-256", input);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function buildSupport(copy: Copy, localeCode: LocaleCode): HTMLElement {
  const main = element("main", "support-page");
  const intro = element("section", "support-intro");
  intro.append(
    element("p", "section-kicker", copy.appName),
    element("h1", "page-title", copy["support-title"]),
    element("p", "document-intro", copy["support-intro"]),
  );

  const form = element("form", "support-form");
  form.noValidate = false;

  const nameGroup = element("div", "field-group");
  const nameLabel = element("label", "", copy["support-name-label"]);
  nameLabel.htmlFor = "name";
  const name = element("input");
  name.id = "name";
  name.name = "name";
  name.type = "text";
  name.autocomplete = "name";
  name.maxLength = 100;
  nameGroup.append(nameLabel, name);

  const emailGroup = element("div", "field-group");
  const emailLabel = element("label", "", copy["support-email-label"]);
  emailLabel.htmlFor = "email";
  const email = element("input");
  email.id = "email";
  email.name = "email";
  email.type = "email";
  email.autocomplete = "email";
  email.inputMode = "email";
  email.maxLength = 254;
  email.required = true;
  emailGroup.append(emailLabel, email);

  const categoryGroup = element("div", "field-group");
  const categoryLabel = element("label", "", copy["support-category-label"]);
  categoryLabel.htmlFor = "category";
  const category = element("select");
  category.id = "category";
  category.name = "category";
  category.required = true;
  const categories = ["general", "bug", "feedback", "privacy", "other"] as const;
  for (const value of categories) {
    const option = element("option", "", copy[`support-category-${value}`]);
    option.value = value;
    category.append(option);
  }
  categoryGroup.append(categoryLabel, category);

  const messageGroup = element("div", "field-group field-wide");
  const messageLabel = element("label", "", copy["support-message-label"]);
  messageLabel.htmlFor = "message";
  const message = element("textarea");
  message.id = "message";
  message.name = "message";
  message.rows = 7;
  message.minLength = 10;
  message.maxLength = 4000;
  message.required = true;
  messageGroup.append(messageLabel, message);

  const honeypotGroup = element("div", "honeypot");
  honeypotGroup.setAttribute("aria-hidden", "true");
  const honeypotLabel = element("label", "", "Company");
  honeypotLabel.htmlFor = "company";
  const honeypot = element("input");
  honeypot.id = "company";
  honeypot.name = "company";
  honeypot.type = "text";
  honeypot.tabIndex = -1;
  honeypot.autocomplete = "off";
  honeypotGroup.append(honeypotLabel, honeypot);

  const footer = element("div", "form-footer field-wide");
  const submit = element("button", "submit-button", copy["support-submit"]);
  submit.type = "submit";
  const status = element("p", "form-status");
  status.id = "form-status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  footer.append(submit, status);

  form.append(nameGroup, emailGroup, categoryGroup, messageGroup, honeypotGroup, footer);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    status.className = "form-status";
    status.textContent = "";

    if (honeypot.value) {
      status.classList.add("success");
      status.textContent = copy["support-success"];
      form.reset();
      return;
    }

    const emailValue = email.value.trim();
    const messageValue = message.value.trim();
    const fingerprint = await messageFingerprint(emailValue, category.value, messageValue);
    const now = Date.now();
    try {
      const previous = JSON.parse(sessionStorage.getItem("chordgen-support-submit") ?? "null") as { at?: number; fingerprint?: string } | null;
      if (previous?.at && (now - previous.at < 20_000 || (previous.fingerprint === fingerprint && now - previous.at < 300_000))) {
        status.classList.add("error");
        status.textContent = copy["support-duplicate"];
        return;
      }
    } catch {
      sessionStorage.removeItem("chordgen-support-submit");
    }

    submit.disabled = true;
    form.setAttribute("aria-busy", "true");
    try {
      const firebase = await supportServices();
      const payload: Record<string, unknown> = {
        email: emailValue,
        category: category.value,
        message: messageValue,
        locale: localeCode,
        createdAt: firebase.serverTimestamp(),
      };
      const nameValue = name.value.trim();
      if (nameValue) payload.name = nameValue;
      await firebase.addDoc(firebase.collection(firebase.database, "messages"), payload);
      sessionStorage.setItem("chordgen-support-submit", JSON.stringify({ at: Date.now(), fingerprint }));
      form.reset();
      status.classList.add("success");
      status.textContent = copy["support-success"];
    } catch (error) {
      console.error("Support form submission failed", error);
      status.classList.add("error");
      status.textContent = copy["support-error"];
    } finally {
      submit.disabled = false;
      form.removeAttribute("aria-busy");
    }
  });

  main.append(intro, form);
  return main;
}

function buildFooter(copy: Copy, locale: (typeof localeDefinitions)[number]): HTMLElement {
  const footer = element("footer", "site-footer");
  const inner = element("div", "footer-inner");
  inner.append(
    element("span", "footer-brand", copy.appName),
    link("", copy["privacy-link"], routeFor(locale, "privacy")),
    link("", copy["support-link"], routeFor(locale, "support")),
  );
  footer.append(inner);
  return footer;
}

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Missing #app root");

const { locale, page } = currentRoute();
const copy = siteContent[locale.code];
document.documentElement.lang = locale.code;
document.documentElement.dir = locale.direction;

app.append(buildHeader(copy, locale, page));
if (page === "privacy") app.append(buildPrivacy(copy));
else if (page === "support") app.append(buildSupport(copy, locale.code));
else app.append(buildHome(copy));
app.append(buildFooter(copy, locale));
